import { buildSanityImageSrcSet, buildSanityImageUrl } from '@lib/sanity/image';
import type { SanityArticleFeaturedImage } from '@lib/sanity/types';
import { isPhoneScreenshot } from '@lib/whats-new/model';

/** The panel a screenshot sits on, wherever an update shows one. */
export const SCREENSHOT_PANEL_CLASS_NAME = 'bg-primary-50';

/** `card` is a listing card or a row of screenshots; `page` is an update's hero. */
export type ScreenshotScale = 'card' | 'page';

const PHONE_FRAME_CLASS_NAMES: Record<ScreenshotScale, string> = {
  card: 'rounded-[18px] border-[4px]',
  page: 'rounded-[30px] border-[7px]'
};

const PHONE_FRAME_CLASS_NAME =
  'border-landing-ink bg-landing-ink shadow-[0_12px_32px_rgba(11,27,43,0.22)]';

const WIDE_FRAME_CLASS_NAME =
  'rounded-lg border border-landing-hairline bg-white shadow-[0_8px_24px_rgba(11,27,43,0.12)]';

const PERCENT = 100;

type ScreenshotImage = Pick<
  SanityArticleFeaturedImage,
  'asset' | 'alt' | 'crop' | 'hotspot'
>;

/**
 * A screenshot scaled to fit its space without cropping. Phone-shaped ones get
 * a dark bezel and rounded corners so they read as a phone; anything wider
 * gets a plain border.
 *
 * The height is worked out from the stored dimensions as the smaller of the
 * cap and the full available width, so the space is held before the file
 * arrives and nothing below it jumps.
 */
export const Screenshot = ({
  image,
  alt = image.alt ?? '',
  scale,
  widths,
  defaultWidth,
  sizes,
  maxHeightClassName,
  loading,
  fetchPriority
}: {
  image: ScreenshotImage;
  alt?: string;
  scale: ScreenshotScale;
  widths: number[];
  defaultWidth: number;
  /** A phone screenshot renders far narrower than a wide one at the same height. */
  sizes: { phone: string; wide: string };
  /** Sets the cap, e.g. `[--screenshot-max-height:320px]`. */
  maxHeightClassName: string;
  loading?: 'lazy';
  fetchPriority?: 'high';
}) => {
  const dimensions = image.asset.metadata?.dimensions;
  const isPhone = isPhoneScreenshot(dimensions);
  const sizeStyle = dimensions
    ? {
        aspectRatio: `${dimensions.width} / ${dimensions.height}`,
        height: `min(var(--screenshot-max-height), ${
          (dimensions.height / dimensions.width) * PERCENT
        }cqw)`
      }
    : { maxHeight: 'var(--screenshot-max-height)' };

  return (
    // The container lets the image size itself against the width it has.
    <span className="block w-full [container-type:inline-size]">
      <img
        src={buildSanityImageUrl(image).width(defaultWidth).fit('max').url()}
        srcSet={buildSanityImageSrcSet(image, widths)}
        sizes={isPhone ? sizes.phone : sizes.wide}
        alt={alt}
        width={dimensions?.width}
        height={dimensions?.height}
        loading={loading}
        decoding="async"
        fetchPriority={fetchPriority}
        data-frame={isPhone ? 'phone' : 'wide'}
        style={sizeStyle}
        // The legacy `.article img` rule adds a bottom margin, which would sit
        // the screenshot off-centre in its panel. The frame's border takes a
        // sliver from the box; contain leaves that gap in the frame's own
        // colour rather than cropping the picture.
        className={`!mb-0 mx-auto block w-auto max-w-full object-contain ${
          isPhone
            ? `${PHONE_FRAME_CLASS_NAME} ${PHONE_FRAME_CLASS_NAMES[scale]}`
            : WIDE_FRAME_CLASS_NAME
        } ${maxHeightClassName}`}
      />
    </span>
  );
};
